/**
 * `/internal/ops/runway/*` contract types (epic #997 / #1004).
 *
 * Two prepaid-runway projections for the morning cockpit:
 *
 * - **Upstream runway** — per-upstream prepaid remaining
 *   (`upstream_funding_batch` capacity − consumed, the same rows behind
 *   FinancePnl's `position.upstream_prepaid_remaining_usd_cents`)
 *   divided by the recent burn rate observed in `revenue_ledger` cost
 *   attribution (usage rows joined to their `source_batch_id` batch,
 *   CNY cost inverted back to USD through the batch's
 *   `effective_rate_cny_per_usd` — the exact inverse of the allocator's
 *   cost computation, so the two surfaces reconcile by construction).
 * - **Customer runway** — per-account remaining credit
 *   (`balance_credit_lots` remaining, the same rows behind FinancePnl's
 *   user-liability position) divided by the account's recent usage burn
 *   from `revenue_ledger`. This is the top-up "chase list".
 *
 * Money encoding follows the ops surface convention: integer USD cents
 * on the wire, micro-USD internally (micros-first dual reads).
 *
 * The unscoped views are brand-agnostic by design: prepaid batches and
 * balance credit lots are not brand-scoped rows, so a brand filter
 * would silently misreport runway. System accounts (`is_system = 1`)
 * are excluded from the customer list (epic #997 AC 2).
 *
 * `/customers` additionally accepts an optional `ownerOpsUserId` param
 * (epic #1035 E6-D2, #1066): the response is then restricted to
 * accounts whose org holds an ACTIVE `customer_manager_assignments`
 * row for that manager (same ownership join as the #1065 revenue
 * aggregates), each row carries `org_id` + exact micros, and the
 * top-N truncation is lifted — the workbench chase list must show
 * EVERY owned customer, not a cockpit top slice.
 *
 * An optional `accountId` (#2155) is the Customer 360 precise read:
 * only that account is computed, top-N is not applied, and a zero-burn
 * account is returned rather than omitted (`days_left: null` = no
 * consumption in the window, runway is infinite). Combined with
 * `ownerOpsUserId`, an account outside that owner's book yields an
 * empty list — the server decides, callers do not filter.
 */

/** Default trailing window (days) used to estimate the burn rate. */
export const RUNWAY_DEFAULT_BURN_DAYS = 7;
export const RUNWAY_MAX_BURN_DAYS = 30;

export const RUNWAY_UPSTREAMS_DEFAULT_LIMIT = 5;
export const RUNWAY_CUSTOMERS_DEFAULT_LIMIT = 10;
export const RUNWAY_MAX_LIMIT = 50;

/** Trailing burn-observation window: `[from, to)`, `to` = request time. */
export interface OpsRunwayBurnWindow {
  readonly from: string;
  readonly to: string;
  readonly days: number;
}

export interface OpsUpstreamRunwayRow {
  /** Cross-DB soft ref to gateway `upstream_configs.id` (ADR-003). The
   * platform cannot see upstream names; consumers decorate via the
   * gateway health snapshot's `upstream_names` map. */
  readonly upstream_id: string;
  /** Prepaid remaining across this upstream's LIVE funding batches, net
   * of overflow debt. Depleted batches are excluded because the batch
   * allocator only ever spends from `is_depleted = 0` rows and nothing
   * ever clears that flag (#1513). */
  readonly remaining_usd_cents: number;
  /**
   * Capacity closed inside this upstream's DEPLETED batches, all-time and
   * always `>= 0`. Not part of `remaining_usd_cents` and not spendable:
   * the allocator never revisits a depleted batch.
   *
   * Non-zero means a batch was closed while USD capacity was still on the
   * row — either its RMB side exhausted first (the allocator's depletion
   * test is `usd_consumed >= usd_capacity OR cny_consumed >= cny_paid`),
   * or its USD counters drifted from the ledger. Both are worth an
   * operator's attention exactly once; neither is money that can be
   * recovered by reopening the batch.
   */
  readonly stranded_usd_cents: number;
  /** Total attributed burn inside the observation window. */
  readonly burn_usd_cents: number;
  readonly burn_per_day_usd_cents: number;
  /** `remaining ÷ (burn ÷ days)`. `null` when the window saw no burn
   * (runway is indeterminate, not infinite). */
  readonly days_left: number | null;
}

export interface OpsUpstreamRunwayResponse {
  readonly generated_at: string;
  readonly burn_window: OpsRunwayBurnWindow;
  /** Sum over ALL upstreams with prepaid remaining (not just the listed
   * top rows). Reconciles with FinancePnl
   * `position.upstream_prepaid_remaining_usd_cents` for the same data. */
  readonly total_remaining_usd_cents: number;
  /**
   * Sum of `stranded_usd_cents` over EVERY upstream — including ones with
   * no live capacity left, which carry no `remaining` and so never reach
   * the ranked list. This total can therefore exceed the sum of the
   * `upstreams` rows, by design: an upstream that has run out entirely is
   * the one whose stranded capacity most needs disclosing.
   */
  readonly total_stranded_usd_cents: number;
  /** Ranked soonest-depleting first: `days_left` ASC nulls-last, then
   * `remaining_usd_cents` ASC, capped at `limit`. */
  readonly upstreams: readonly OpsUpstreamRunwayRow[];
}

export interface OpsCustomerRunwayRow {
  readonly account_id: string;
  readonly account_email: string;
  /** Remaining credit across the account's balance lots. Can be 0 while
   * `burn > 0` — an account that already ran dry but kept trying. */
  readonly remaining_usd_cents: number;
  readonly burn_usd_cents: number;
  readonly burn_per_day_usd_cents: number;
  /**
   * `remaining ÷ (burn ÷ days)`. Chase-list rows are always finite:
   * accounts with no burn in the window are omitted (nothing to chase).
   * Precise `accountId` reads return `null` when the window saw no
   * consumption — infinite runway, not missing data.
   */
  readonly days_left: number | null;
  /**
   * Owner-scoped requests (`ownerOpsUserId`, #1066) and precise
   * `accountId` reads that also carry an owner: the org whose active
   * assignment put this account in the manager's book. Absent on
   * unscoped cockpit rows.
   */
  readonly org_id?: string | null;
  /** Owner-scoped or precise `accountId` reads: exact micro-USD twins
   * of the cents fields, so Customer 360 keeps full precision. */
  readonly remaining_usd_micros?: number;
  readonly burn_usd_micros?: number;
}

export interface OpsCustomerRunwayResponse {
  readonly generated_at: string;
  readonly burn_window: OpsRunwayBurnWindow;
  /**
   * Ranked `days_left` ASC (soonest to run out first), capped at
   * `limit` — except owner-scoped requests, which return the WHOLE
   * owned book (#1066), and precise `accountId` reads, which return
   * that one account (or empty) and never a top-N slice. Chase-list
   * rows are non-system, non-deleted accounts with credit lots and
   * positive burn; a precise read may also return a zero-burn row.
   */
  readonly customers: readonly OpsCustomerRunwayRow[];
}
